import type {
  AccessLevel,
  ContactAccessShareInput,
  MyContactAccess,
  RecordRights,
  ReassignKeep,
} from '@ehr/shared';
import { api } from './api.js';

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11): the Share & Access API (apps/api contact-access.route).
 * Every rule (who may share, up to their own access, per-tab levels) is the server's; these are
 * just the calls.
 */
export interface AccessPerson {
  id: string;
  name: string;
  role: string;
}

export interface UserGrantRow {
  userId: string;
  name: string;
  role: string;
  /** Saved before per-tab access: every tab, no limits. */
  legacy: boolean;
  tabs: Record<string, AccessLevel> | null;
  rights: RecordRights;
  summary: string;
  rightsSummary: string;
}

export interface RoleGrantRow {
  role: string;
  label: string;
  members: { id: string; name: string }[];
  excluded: { id: string; name: string }[];
  tabs: Record<string, AccessLevel>;
  rights: RecordRights;
  summary: string;
  rightsSummary: string;
}

export interface WhoHasAccess {
  contact: {
    id: string;
    name: string;
    category: string;
    ownerUserId: string | null;
    ownerName: string | null;
    ownerRole: string | null;
  };
  mine: { canReassign: boolean; admin: boolean; owner: boolean };
  users: UserGrantRow[];
  roles: RoleGrantRow[];
  history: { at: string; by: string; action: string }[];
}

export type ShareBody = ContactAccessShareInput;

export const fetchAccessPeople = () => api.get<AccessPerson[]>('/contacts/access/people');
export const fetchMyAccess = (contactId: string) =>
  api.get<MyContactAccess>(`/contacts/${contactId}/access/mine`);
export const fetchWhoHasAccess = (contactId: string) =>
  api.get<WhoHasAccess>(`/contacts/${contactId}/access`);
export const postShare = (body: ShareBody) =>
  api.post<{ contacts: number; grants: number }>('/contacts/access/share', body);
export const postReassign = (body: {
  contactIds: string[];
  ownerUserId: string;
  keep: ReassignKeep;
}) => api.post<{ reassigned: number }>('/contacts/access/reassign', body);
export const removeUserAccess = (contactId: string, userId: string) =>
  api.del(`/contacts/${contactId}/access/users/${userId}`);
export const removeRoleAccess = (contactId: string, role: string) =>
  api.del(`/contacts/${contactId}/access/roles/${role}`);
